// ---------------------------------------------------------------------------
// Prezio Onboard Widget — StepRouter
// ---------------------------------------------------------------------------
// Core routing component. Maps session.current_step to the correct step
// component, handles options loading, auto-select, animated transitions,
// and focus management.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { StepId } from '@/api/types';
import { useSessionState, useSessionActions } from '@/hooks/useSession';
import { useStepOptions } from '@/hooks/useStepOptions';
import { usePrefetchDsoOptions } from '@/hooks/usePrefetchDsoOptions';
import { t } from '@/i18n';
import { STEP_ORDER, getVisibleSteps, getPredefinedSet } from '@/utils/constants';
import { SkeletonLoader } from '@/components/shared/SkeletonLoader';
import { CompletionStep } from '@/components/steps/CompletionStep';
import { ErrorStep } from '@/components/steps/ErrorStep';
import type { StepProps } from '@/types/steps';

// Step component imports
import { CountryStep } from '@/components/steps/CountryStep';
import { ConsumerTypeStep } from '@/components/steps/ConsumerTypeStep';
import { AddressStep } from '@/components/steps/AddressStep';
import { AddressValidationStep } from '@/components/steps/AddressValidationStep';
import { OrganizationStep } from '@/components/steps/OrganizationStep';
import { MainTariffStep } from '@/components/steps/MainTariffStep';
import { TariffStep } from '@/components/steps/TariffStep';
import { AuthoritiesStep } from '@/components/steps/AuthoritiesStep';
import { PropertiesStep } from '@/components/steps/PropertiesStep';
import { CustomTypeStep } from '@/components/steps/CustomTypeStep';
import { CustomPropertiesStep } from '@/components/steps/CustomPropertiesStep';
import { CustomPricesStep } from '@/components/steps/CustomPricesStep';
import { SignupDateStep } from '@/components/steps/SignupDateStep';
import { SummaryStep } from '@/components/steps/SummaryStep';

// ---------------------------------------------------------------------------
// Step → Component mapping
// ---------------------------------------------------------------------------

const STEP_MAP: Record<StepId, ComponentType<StepProps>> = {
  country: CountryStep,
  local_consumer_type: ConsumerTypeStep,
  address: AddressStep,
  address_validation: AddressValidationStep,
  dso_org: OrganizationStep,
  dso_main_tariff: MainTariffStep,
  dso_tariff: TariffStep,
  dso_properties: PropertiesStep,
  retailer_org: OrganizationStep,
  retailer_main_tariff: MainTariffStep,
  retailer_signup_date: SignupDateStep,
  retailer_tariff: TariffStep,
  retailer_properties: PropertiesStep,
  retailer_custom_type: CustomTypeStep,
  retailer_custom_properties: CustomPropertiesStep,
  retailer_custom_prices: CustomPricesStep,
  authorities_conditions: PropertiesStep,
  authorities: AuthoritiesStep,
  summary: SummaryStep,
};


// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const SLIDE_OFFSET = 60;

function getDirection(
  from: StepId | null,
  to: StepId,
  visibleSteps: readonly StepId[] = STEP_ORDER,
): 'forward' | 'backward' {
  if (!from) return 'forward';
  const fromIdx = visibleSteps.indexOf(from);
  const toIdx = visibleSteps.indexOf(to);
  return toIdx >= fromIdx ? 'forward' : 'backward';
}

const variants = {
  enter: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? SLIDE_OFFSET : -SLIDE_OFFSET,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: 'forward' | 'backward') => ({
    x: direction === 'forward' ? -SLIDE_OFFSET : SLIDE_OFFSET,
    opacity: 0,
  }),
};

const reducedMotionVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

const TRANSITION = { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] };
const INSTANT = { duration: 0 };

// ---------------------------------------------------------------------------
// StepRouter
// ---------------------------------------------------------------------------

export function StepRouter() {
  const { session, isSubmitting, error } = useSessionState();
  const { submitStep, completeSession } = useSessionActions();

  const currentStep = session?.current_step ?? null;
  const prevStepRef = useRef<StepId | null>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const completionAttemptedRef = useRef(false);
  const autoSubmitRef = useRef<StepId | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Visible steps exclude any predefined (hidden) steps
  const visibleSteps = getVisibleSteps(session?.predefined_steps);
  const predefined = getPredefinedSet(session?.predefined_steps);

  const direction = currentStep
    ? getDirection(prevStepRef.current, currentStep, visibleSteps)
    : 'forward';

  // On the very first visible step (no previous step tracked) skip the
  // entrance animation so the widget doesn't slide in from nowhere. This
  // is especially important when predefined steps push the initial step
  // further into the flow — the user should see it appear instantly.
  const isFirstRender = prevStepRef.current === null;

  // Prefetch DSO step options while the user is on retailer steps
  usePrefetchDsoOptions(currentStep);

  // Auto-submit predefined steps — when the backend advances current_step
  // to a step whose data was pre-provisioned at session creation, replay
  // the collected_data so the backend advances past it. This can chain
  // through multiple consecutive predefined steps (e.g. address → address_validation).
  useEffect(() => {
    if (!session || !currentStep || isSubmitting) return;
    if (!predefined.has(currentStep)) return;
    if (autoSubmitRef.current === currentStep) return;
    autoSubmitRef.current = currentStep;

    const payload = buildPredefinedPayload(currentStep, session.collected_data);
    if (payload) {
      void submitStep(currentStep, payload).catch(() => {
        // Error lands in state — the predefined skeleton guard below
        // yields to the normal error handling path.
      });
    }
  }, [session, currentStep, isSubmitting, predefined, submitStep]);

  // Track previous step for direction calculation
  useEffect(() => {
    if (currentStep) {
      prevStepRef.current = currentStep;
    }
  }, [currentStep]);

  // Focus the step heading on step change
  useEffect(() => {
    if (currentStep) {
      // Delay to let animation start
      const timer = setTimeout(() => headingRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Submit handler passed to step components
  const handleSubmit = useCallback(
    (data: Record<string, unknown>) => {
      if (!currentStep) return;
      void submitStep(currentStep, data)
        .then((session) => {
          // After summary confirmation the backend keeps current_step as
          // 'summary' and expects POST /complete/ to finalize. Chain it.
          if (currentStep === 'summary' && session.status === 'in_progress') {
            completionAttemptedRef.current = true;
            return completeSession();
          }
        })
        .catch(() => {
          // Errors are already dispatched to state by submitStep / completeSession.
          // This catch prevents unhandled promise rejections.
        });
    },
    [currentStep, submitStep, completeSession],
  );

  // Auto-complete: call POST /complete/ when the step chain is done.
  // Triggers when current_step becomes null while session is still in_progress
  // (backend has no more steps). Guard prevents re-entry — once attempted,
  // the user must explicitly retry via the SummaryStep "Retry" button which
  // re-submits { confirmed: true } to reset the flow.
  useEffect(() => {
    if (!session || isSubmitting || session.status !== 'in_progress') return;
    if (completionAttemptedRef.current) return;
    if (session.current_step !== null) return;

    completionAttemptedRef.current = true;
    void completeSession().catch(() => {
      // Don't reset the guard — prevents infinite retry loop.
      // The error lands in SessionContext state; SummaryStep shows
      // an error banner + "Retry" button that re-submits confirmation.
    });
  }, [session, isSubmitting, completeSession]);

  // --- Terminal states ---

  if (session?.status === 'completed') {
    return <CompletionStep />;
  }

  if (error && (error.status === 401 || error.status === 404)) {
    return <ErrorStep message={error.message} />;
  }

  if (session?.status === 'expired') {
    return <ErrorStep message={t('error.sessionExpired')} />;
  }

  if (!currentStep) {
    // If completion was attempted but failed, show error + retry
    if (completionAttemptedRef.current && error) {
      return (
        <CompletionErrorRetry error={error} isSubmitting={isSubmitting} onRetry={completeSession} />
      );
    }
    return <SkeletonLoader variant="card" />;
  }

  // Predefined step — show skeleton while auto-submit is in flight.
  // Skip this guard when an error is present so the normal error UI renders.
  if (predefined.has(currentStep) && !error) {
    return <SkeletonLoader variant="card" />;
  }

  const activeVariants = prefersReducedMotion ? reducedMotionVariants : variants;
  const activeTransition = prefersReducedMotion || isFirstRender ? INSTANT : TRANSITION;

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={currentStep}
        custom={direction}
        variants={activeVariants}
        initial={isFirstRender ? false : 'enter'}
        animate="center"
        exit="exit"
        transition={activeTransition}
      >
        <StepContent
          stepId={currentStep}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          headingRef={headingRef}
        />
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// CompletionErrorRetry — shown when POST /complete/ fails
// ---------------------------------------------------------------------------

interface CompletionErrorRetryProps {
  error: { message?: string };
  isSubmitting: boolean;
  onRetry: () => Promise<unknown>;
}

/** Renders an error message + retry button when session completion fails. */
function CompletionErrorRetry({ error, isSubmitting, onRetry }: CompletionErrorRetryProps) {
  return (
    <div style={{ padding: 'var(--po-space-6)', textAlign: 'center' }}>
      <p style={{ color: 'var(--po-color-text-secondary)', marginBottom: 'var(--po-space-4)' }}>
        {error.message || t('error.fallback')}
      </p>
      <button
        type="button"
        onClick={() => void onRetry()}
        disabled={isSubmitting}
        style={{
          padding: 'var(--po-space-3) var(--po-space-6)',
          borderRadius: 'var(--po-radius-md)',
          background: 'var(--po-color-primary)',
          color: 'var(--po-color-primary-foreground)',
          fontWeight: 600,
          cursor: isSubmitting ? 'wait' : 'pointer',
          border: 'none',
          opacity: isSubmitting ? 0.7 : 1,
        }}
      >
        {isSubmitting ? t('common.loading') : t('common.retry')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepContent — handles options loading + auto-select per step
// Auto-resolution of single-option / zero-option steps now happens
// server-side in `update_session`, so the widget never receives a
// `skip_step=true` response in practice.
// ---------------------------------------------------------------------------

interface StepContentProps {
  stepId: StepId;
  onSubmit: (data: Record<string, unknown>) => void;
  isSubmitting: boolean;
  headingRef: React.RefObject<HTMLDivElement>;
}

function StepContent({
  stepId,
  onSubmit,
  isSubmitting,
  headingRef,
}: StepContentProps) {
  const { options, isLoading, error, refetch } = useStepOptions(stepId);

  if (isLoading) {
    return <SkeletonLoader variant={getSkeletonVariant(stepId)} />;
  }

  // Show retry UI if options failed to load
  if (error || !options) {
    return (
      <div style={{ padding: 'var(--po-space-6)', textAlign: 'center' }}>
        <p style={{ color: 'var(--po-color-text-secondary)', marginBottom: 'var(--po-space-4)' }}>
          {error?.message ?? t('error.fallback')}
        </p>
        <button
          type="button"
          onClick={refetch}
          style={{
            padding: 'var(--po-space-3) var(--po-space-6)',
            borderRadius: 'var(--po-radius-md)',
            background: 'var(--po-color-primary)',
            color: 'var(--po-color-primary-foreground)',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
          }}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const StepComponent = STEP_MAP[stepId];
  return (
    <div ref={headingRef} tabIndex={-1} style={{ outline: 'none' }}>
      <StepComponent
        stepId={stepId}
        options={options}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSkeletonVariant(stepId: StepId): 'card' | 'form' | 'summary' {
  if (stepId === 'summary') return 'summary';
  if (stepId === 'address' || stepId === 'address_validation') return 'form';
  return 'card';
}

/**
 * Build the PATCH payload for a predefined step from existing collected_data.
 * Returns `null` if the required data is missing.
 */
function buildPredefinedPayload(
  step: StepId,
  collectedData: Record<string, unknown>,
): Record<string, unknown> | null {
  switch (step) {
    case 'country': {
      const cc = collectedData['country_code'];
      return typeof cc === 'string' ? { country_code: cc } : null;
    }
    case 'local_consumer_type': {
      const id = collectedData['local_consumer_type_id'];
      return typeof id === 'string' ? { local_consumer_type_id: id } : null;
    }
    case 'address': {
      const addr = collectedData['address'];
      if (typeof addr === 'object' && addr !== null) {
        const a = addr as Record<string, unknown>;
        // Backend stores the display value as 'formatted_address' in collected_data,
        // but the submit payload expects the 'display' key.
        const display = typeof a['formatted_address'] === 'string' ? a['formatted_address']
          : typeof a['display'] === 'string' ? a['display']
          : '';
        return {
          display,
          address1: typeof a['address1'] === 'string' ? a['address1'] : undefined,
          city: typeof a['city'] === 'string' ? a['city'] : undefined,
          postal_code: typeof a['postal_code'] === 'string' ? a['postal_code'] : undefined,
        };
      }
      // Coordinates-only pre-provisioning
      const coords = collectedData['coordinates'];
      if (typeof coords === 'object' && coords !== null) {
        return { coordinates: coords };
      }
      return null;
    }
    case 'address_validation':
      return { confirmed: true };
    default:
      return null;
  }
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduced;
}
